
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/line'
import 'echarts/lib/chart/scatter'
import 'echarts/lib/component/markArea'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/grid'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'
import moment from 'moment'

import TextBox from '@/components/TextBox.vue'
import { zoneBounds } from '@/utils/budgetSimulator.js'

// The zone colours of the per-channel saturation curves (SaturationCurves.vue).
const ZONE_COLORS = ['rgba(213, 244, 254, 0.8)', 'rgba(235, 247, 225, 1)', 'rgba(255, 243, 215, 1)', 'rgba(255, 216, 219, 1)']

export default {
  components: {
    'v-chart': ECharts,
    TextBox
  },
  props: {
    // Finished grid points: [{ budget, marketingSales }], sorted by budget.
    rows: {
      type: Array,
      required: true
    },
    // One per period with data: [{ x, y, date, spend, sales, scale }], x and y annualized (see historicalPoints).
    historical: {
      type: Array,
      required: false,
      default: () => []
    },
    regions: {
      type: Array,
      required: false,
      default: null
    },
    title: {
      type: String,
      required: true
    },
    spendUnitName: {
      type: String,
      required: false,
      default: ''
    },
    outputUnitName: {
      type: String,
      required: false,
      default: ''
    },
    frequency: {
      type: String,
      required: false,
      default: 'month'
    },
    exportImageName: {
      type: String,
      required: false,
      default: 'Total Saturation Curve'
    }
  },
  data () {
    return {
      showHistorical: true
    }
  },
  computed: {
    zonesAvailable () {
      return !!(this.regions && this.regions.length === 4)
    },
    zoneLegend () {
      return [2, 3, 4, 5].map((zone, i) => ({
        key: zone,
        color: ZONE_COLORS[i],
        label: this.$t(`components.SaturationCurves.zone${zone}`)
      }))
    },
    curveData () {
      return [[0, 0]].concat(this.rows.map(row => [row.budget, row.marketingSales]))
    },
    maxX () {
      const xs = this.curveData.map(point => point[0])
      if (this.showHistorical) {
        this.historical.forEach(point => xs.push(point.x))
      }
      return Math.max.apply(null, xs)
    },
    markArea () {
      if (!this.zonesAvailable) return null
      // The zones are always shown; a collapsed one (from === to) is left out.
      const zones = zoneBounds(this.regions, this.maxX)
      return {
        silent: true,
        data: [0, 1, 2, 3]
          .filter(i => zones[i][1] > zones[i][0])
          .map(i => [
            { xAxis: zones[i][0], itemStyle: { color: ZONE_COLORS[i] } },
            { xAxis: zones[i][1] }
          ])
      }
    },
    options () {
      const series = [{
        name: this.$t('components.budgetSimulator.TotalSaturationCurve.simulated'),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 7,
        lineStyle: { width: 3, color: '#6950a1' },
        itemStyle: { color: '#6950a1' },
        data: this.curveData,
        markArea: this.markArea || undefined,
        z: 3
      }]
      if (this.showHistorical && this.historical.length > 0) {
        series.push({
          name: this.$t('components.budgetSimulator.TotalSaturationCurve.historical'),
          type: 'scatter',
          symbolSize: 8,
          itemStyle: { color: '#ff9800', borderColor: 'white', borderWidth: 1 },
          data: this.historical.map(point => ({ value: [point.x, point.y], date: point.date, spend: point.spend, sales: point.sales, scale: point.scale })),
          z: 4
        })
      }
      return {
        toolbox: {
          feature: {
            saveAsImage: { type: 'png', name: this.exportImageName, title: this.$t('components.BarChart.saveAsImage') }
          },
          showTitle: false
        },
        legend: { top: 0 },
        grid: { left: 80, right: 40, top: 40, bottom: 60 },
        tooltip: {
          trigger: 'item',
          formatter: this.formatTooltip
        },
        xAxis: {
          type: 'value',
          name: `${this.$t('components.budgetSimulator.TotalSaturationCurve.budget')} (${this.spendUnitName})`,
          nameLocation: 'middle',
          nameGap: 30,
          splitLine: { show: false },
          axisLabel: { formatter: this.formatValue }
        },
        yAxis: {
          type: 'value',
          name: `${this.$t('components.budgetSimulator.TotalSaturationCurve.attributedSales')} (${this.outputUnitName})`,
          nameLocation: 'middle',
          nameGap: 55,
          splitLine: { show: false },
          axisLabel: { formatter: this.formatValue }
        },
        series: series
      }
    }
  },
  methods: {
    formatValue (value) {
      return numbro(value).format({ average: true, mantissa: 1, optionalMantissa: true })
    },
    formatDate (date) {
      return this.frequency === 'week' ? moment(date).format('ll') : moment(date).format('MMM YYYY')
    },
    // A historical point's period: the week by the Sunday that ends it, or the month.
    periodLabel (date) {
      if (this.frequency === 'week') {
        return this.$t('components.budgetSimulator.TotalSaturationCurve.weekEnding', { date: this.formatDate(date) })
      }
      return this.formatDate(date)
    },
    formatTooltip (param) {
      const value = param.value
      const budgetLabel = this.$t('components.budgetSimulator.TotalSaturationCurve.budget')
      const salesLabel = this.$t('components.budgetSimulator.TotalSaturationCurve.attributedSales')
      const roas = value[0] > 0 ? numbro(value[1] / value[0]).format({ mantissa: 2 }) : '-'
      // A historical point: the period's own figures, and what they come to annualized.
      if (param.data && param.data.date) {
        const scaled = figure => `${this.formatValue(figure)} (× ${param.data.scale} = ${this.formatValue(figure * param.data.scale)})`
        return `<b>${this.periodLabel(param.data.date)}</b><br>${budgetLabel}: ${scaled(param.data.spend)} ${this.spendUnitName}<br>${salesLabel}: <span style="color: lightblue;">${scaled(param.data.sales)} ${this.outputUnitName}</span><br>ROAS: ${roas}`
      }
      return `<b>${budgetLabel}: ${this.formatValue(value[0])} ${this.spendUnitName}</b><br>${salesLabel}: <span style="color: lightblue;">${this.formatValue(value[1])} ${this.outputUnitName}</span><br>ROAS: ${roas}`
    }
  }
}
